import {
  Routes,
  Route,
  Link,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { useState, useEffect, useRef, useSyncExternalStore, lazy, Suspense } from 'react';
import {
  ShieldIcon,
  THEME_NAMES,
  themes,
  useTheme,
  initTheme,
  applyThemeAttribute,
  isThemeName,
  type ThemeChoice,
} from '@threatlens/design-system';
/**
 * Route-level code splitting.
 *
 * 37 of 38 pages used to be statically imported here, which put every one of them into the entry
 * chunk: 936 KB for a visitor who only opens the homepage. The heavy libraries were already split
 * (cytoscape, the ATT&CK reference, noVNC), so the weight was the pages themselves.
 *
 * Measured: 936 KB -> 428 KB entry, and it now clears Vite's 500 KB warning. Only `SubmitPage` stays
 * eager, because it IS the homepage - lazily loading it would put a spinner on the first paint of the
 * most-visited route for no benefit. Everything else, including the report page, is fetched when its
 * route is first visited; a deep link pays one extra request that arrives alongside a much smaller
 * entry chunk rather than after a bigger one.
 *
 * The <Suspense> boundary that makes this work already existed - it was added for LiveTaskPage.
 */
import { SubmitPage } from './pages/SubmitPage.js';
const MalwareAnalysisPage = lazy(() => import('./pages/LandingPages.js').then((m) => ({ default: m.MalwareAnalysisPage })));
const SandboxPage = lazy(() => import('./pages/LandingPages.js').then((m) => ({ default: m.SandboxPage })));
const ThreatIntelligencePage = lazy(() => import('./pages/LandingPages.js').then((m) => ({ default: m.ThreatIntelligencePage })));
const UrlScannerPage = lazy(() => import('./pages/LandingPages.js').then((m) => ({ default: m.UrlScannerPage })));
const TrendsPage = lazy(() => import('./pages/TrendsPage.js').then((m) => ({ default: m.TrendsPage })));
const LinkDevicePage = lazy(() => import('./pages/LinkDevicePage.js').then((m) => ({ default: m.LinkDevicePage })));
const ScanPage = lazy(() => import('./pages/ScanPage.js').then((m) => ({ default: m.ScanPage })));
const ReportPage = lazy(() => import('./pages/ReportPage.js').then((m) => ({ default: m.ReportPage })));
const CommunityPage = lazy(() => import('./pages/CommunityPage.js').then((m) => ({ default: m.CommunityPage })));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage.js').then((m) => ({ default: m.LeaderboardPage })));
const ResearcherProfilePage = lazy(() => import('./pages/ResearcherProfilePage.js').then((m) => ({ default: m.ResearcherProfilePage })));
const AuthPage = lazy(() => import('./pages/AuthPage.js').then((m) => ({ default: m.AuthPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage.js').then((m) => ({ default: m.DashboardPage })));
const EmbedPage = lazy(() => import('./pages/EmbedPage.js').then((m) => ({ default: m.EmbedPage })));
const VerifyPage = lazy(() => import('./pages/VerifyPage.js').then((m) => ({ default: m.VerifyPage })));
// LiveTaskPage pulls in noVNC, whose crypto uses top-level await. Lazy-load it so that
// top-level await lands in a /live-only chunk instead of the app entry. A top-level-await
// entry module is an async module that fails to boot (blank page) on some Android browsers
// and in-app WebViews, which is why the site "did not open" there.
const LiveTaskPage = lazy(() =>
  import('./pages/LiveTaskPage.js').then((m) => ({ default: m.LiveTaskPage })),
);
const ReplayPage = lazy(() => import('./pages/ReplayPage.js').then((m) => ({ default: m.ReplayPage })));
const GraphExplorerPage = lazy(() => import('./pages/GraphExplorerPage.js').then((m) => ({ default: m.GraphExplorerPage })));
const FamilyPage = lazy(() => import('./pages/FamilyPage.js').then((m) => ({ default: m.FamilyPage })));
const BrandPage = lazy(() => import('./pages/BrandPage.js').then((m) => ({ default: m.BrandPage })));
const BrandsIndexPage = lazy(() => import('./pages/BrandsIndexPage.js').then((m) => ({ default: m.BrandsIndexPage })));
const BrandsSearchPage = lazy(() => import('./pages/BrandsSearchPage.js').then((m) => ({ default: m.BrandsSearchPage })));
const AttackNavigatorPage = lazy(() => import('./pages/AttackNavigatorPage.js').then((m) => ({ default: m.AttackNavigatorPage })));
const FirehosePage = lazy(() => import('./pages/FirehosePage.js').then((m) => ({ default: m.FirehosePage })));
const TaskWatchPage = lazy(() => import('./pages/TaskWatchPage.js').then((m) => ({ default: m.TaskWatchPage })));
const SearchPage = lazy(() => import('./pages/SearchPage.js').then((m) => ({ default: m.SearchPage })));
const CollectionsPage = lazy(() => import('./pages/CollectionsPage.js').then((m) => ({ default: m.CollectionsPage })));
const AdminOpsPage = lazy(() => import('./pages/AdminOpsPage.js').then((m) => ({ default: m.AdminOpsPage })));
const StatusPage = lazy(() => import('./pages/StatusPage.js').then((m) => ({ default: m.StatusPage })));
const ApiDocsPage = lazy(() => import('./pages/ApiDocsPage.js').then((m) => ({ default: m.ApiDocsPage })));
import { AgentChatWidget } from './components/AgentChatWidget.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { NotificationBell } from './components/NotificationBell.js';
import { OnboardingChecklist } from './components/OnboardingChecklist.js';
const AlertsPage = lazy(() => import('./pages/AlertsPage.js').then((m) => ({ default: m.AlertsPage })));
const DiffPage = lazy(() => import('./pages/DiffPage.js').then((m) => ({ default: m.DiffPage })));
const RulesPage = lazy(() => import('./pages/RulesPage.js').then((m) => ({ default: m.RulesPage })));
const CampaignPage = lazy(() => import('./pages/CampaignPage.js').then((m) => ({ default: m.CampaignPage })));
const VaultPage = lazy(() => import('./pages/VaultPage.js').then((m) => ({ default: m.VaultPage })));
const CommunityExchangePage = lazy(() => import('./pages/CommunityExchangePage.js').then((m) => ({ default: m.CommunityExchangePage })));
const TermsPage = lazy(() => import('./pages/TermsPage.js').then((m) => ({ default: m.TermsPage })));
const VaultReportsPage = lazy(() => import('./pages/VaultReportsPage.js').then((m) => ({ default: m.VaultReportsPage })));
const BillingPage = lazy(() => import('./pages/BillingPage.js').then((m) => ({ default: m.BillingPage })));
const StyleGuidePage = lazy(() => import('./pages/StyleGuidePage.js').then((m) => ({ default: m.StyleGuidePage })));
import { CommandPalette } from './components/CommandPalette.js';
const AccountActionPage = lazy(() => import('./pages/AccountActionPage.js').then((m) => ({ default: m.AccountActionPage })));
const ComparePage = lazy(() => import('./pages/ComparePage.js').then((m) => ({ default: m.ComparePage })));
const PricingPage = lazy(() => import('./pages/PricingPage.js').then((m) => ({ default: m.PricingPage })));
const PartnerPage = lazy(() => import('./pages/PartnerPage.js').then((m) => ({ default: m.PartnerPage })));
import { api, getSession, getSessionRoles, loadServerSession, sessionRolesKnown, signOut, subscribeSession } from './lib/api.js';
import { captureReferral } from './lib/growth.js';

/** Stable per-browser visitor id (random, not PII) so anonymous presence isn't double-counted. */
function getVisitorId(): string {
  try {
    let id = localStorage.getItem('ma_visitor_id');
    if (!id) {
      id = (crypto.randomUUID?.() ?? `v-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`).replace(/[^A-Za-z0-9_-]/g, '');
      localStorage.setItem('ma_visitor_id', id);
    }
    return id;
  } catch {
    return 'anon';
  }
}

function FooterColumn({ title, links }: { title: string; links: [string, string][] }) {
  return (
    <div>
      <div className="text-micro font-semibold uppercase tracking-wider text-white/45">{title}</div>
      <ul className="mt-3 space-y-2">
        {links.map(([to, label]) => (
          <li key={to}>
            <Link to={to} className="text-sm text-white/70 transition-colors hover:text-white">
              {label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Catch-all 404 so an unknown/mistyped path renders a branded page, not a blank main area. */
function NotFound() {
  return (
    <div className="py-16 text-center">
      <div className="text-4xl font-semibold text-text-primary">404</div>
      <p className="mt-2 text-sm text-text-secondary">This page doesn&apos;t exist or has moved.</p>
      <Link
        to="/"
        className="mt-6 inline-block rounded-md bg-cta px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-cta-hover"
      >
        Go to homepage
      </Link>
    </div>
  );
}

/**
 * The Agent is now a global floating chatbot, not a dedicated page. Any navigation to /agent - the
 * nav link, or report/scan "Ask agent" deep links carrying ?q= - just opens the chat widget and
 * returns the user to where they were, so every existing entry point keeps working unchanged.
 */
function AgentRoute() {
  const nav = useNavigate();
  useEffect(() => {
    // The Agent is registered-users-only; send anonymous visitors to sign in (as the old page did).
    // For signed-in users this renders nothing: the global AgentChatWidget watches the location, opens
    // the chat, and redirects away - so there is no dedicated page and no cross-component race.
    // Carry WHY, so the sign-in form explains what the visitor asked for instead of looking like an
    // expired session, and where to return to once they are in.
    if (!getSession().accessToken) nav('/auth?reason=agent&next=/agent', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

/** Swatch dot for the theme picker (System renders the light/dark pair split). */
function ThemeSwatch({ choice }: { choice: ThemeChoice }) {
  if (choice === 'system') {
    return (
      <span
        aria-hidden
        className="h-3.5 w-3.5 shrink-0 rounded-full border border-white/40"
        style={{ background: `linear-gradient(135deg, ${themes.light.swatch.bg} 50%, ${themes.dark.swatch.bg} 50%)` }}
      />
    );
  }
  const s = themes[choice].swatch;
  return (
    <span
      aria-hidden
      className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border border-white/40"
      style={{ backgroundColor: s.bg }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: s.accent }} />
    </span>
  );
}

/** Sidebar theme picker: System + the six palettes, persisted per browser. */
function ThemePicker() {
  const { choice, setChoice } = useTheme();
  const ref = useRef<HTMLDetailsElement | null>(null);
  const options: { id: ThemeChoice; label: string }[] = [
    { id: 'system', label: 'System' },
    ...THEME_NAMES.map((n) => ({ id: n, label: themes[n].label })),
  ];
  const current = options.find((o) => o.id === choice) ?? { id: 'system' as ThemeChoice, label: 'System' };
  return (
    <details ref={ref} className="relative">
      <summary className="flex cursor-pointer list-none items-center justify-between rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-sm text-white/70 transition-colors hover:border-white/30 hover:text-white">
        <span className="flex items-center gap-2">
          <ThemeSwatch choice={current.id} />
          Theme: {current.label}
        </span>
        <span aria-hidden className="text-white/50">▴</span>
      </summary>
      <div className="absolute bottom-full left-0 z-50 mb-1 w-full rounded-md border border-white/15 bg-brand-navy p-1 shadow-lg">
        {options.map((o) => (
          <button
            key={o.id}
            onClick={() => {
              setChoice(o.id);
              ref.current?.removeAttribute('open');
            }}
            className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors ${
              o.id === choice ? 'bg-white/10 font-medium text-white' : 'text-white/70 hover:bg-white/5 hover:text-white'
            }`}
          >
            <ThemeSwatch choice={o.id} />
            {o.label}
            {o.id === choice && <span aria-hidden className="ml-auto text-white/70">✓</span>}
          </button>
        ))}
      </div>
    </details>
  );
}

/** Small monochrome trust badge (affiliations + certifications held by Cyble). */
function TrustBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded border border-white/25 px-2 py-1 text-micro font-medium text-white/85">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M20 6L9 17l-5-5" />
      </svg>
      {label}
    </span>
  );
}

export function App() {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const nav = useNavigate();
  const location = useLocation();
  // Subscribe to session changes so sign-in/sign-out re-renders the chrome (nav, account group)
  // immediately - the session lives outside React, so a plain read here goes stale after signOut.
  const session = useSyncExternalStore(subscribeSession, getSession);
  const authed = !!session.accessToken;

  // Close the mobile nav drawer whenever the route changes (tapping a link navigates + closes).
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);
  // Capture a referral code / UTM source once on first load so signup can attribute it (growth loop).
  useEffect(() => {
    captureReferral(window.location.search);
  }, []);
  // Live-presence heartbeat: mark this visitor online (registered if signed in, else anonymous) on
  // load and every 45s, plus when the tab regains focus, so the platform's "users online" count is
  // real-time and covers every active visitor on any page. Best-effort; failures are ignored.
  useEffect(() => {
    const vid = getVisitorId();
    const ping = () => {
      void api.presencePing(vid).catch(() => undefined);
    };
    ping();
    const timer = setInterval(ping, 45_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') ping();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
  // Theme boot: re-sync with the persisted choice (the index.html no-flash script already
  // applied it) and track OS light/dark changes while on "system". Embeds render inside
  // third-party iframes and must not inherit the host browser's stored choice: they default
  // to light with an optional ?theme= override.
  const isEmbed = location.pathname.startsWith('/embed/');
  useEffect(() => {
    if (!isEmbed) {
      initTheme();
      return;
    }
    const param = new URLSearchParams(location.search).get('theme');
    applyThemeAttribute(isThemeName(param) ? param : 'light');
  }, [isEmbed, location.search]);

  // The admin console is a cross-tenant platform surface. Only the platform-admin role (granted by
  // PLATFORM_ADMIN_EMAILS) may see the nav link or reach the route. Org-scoped `owner`/`admin` are
  // self-serve on signup, so they must not unlock it. The server enforces the same, this just
  // avoids showing a dead link + firing 403/503 calls for ordinary users.
  // Roles come from the server in cookie mode, where the client cannot decode its own token. Runs on
  // boot and on every sign-in/sign-out, and is a cheap no-op in bearer mode.
  useEffect(() => {
    void loadServerSession();
  }, [session]);

  const isPlatformAdmin = getSessionRoles().includes('platform-admin');

  // Sample Vault is behind the admin-tunable `sample_vault_enabled` flag (surfaced on /v1/status).
  // The nav entry + route only appear when the platform reports it on (default OFF).
  const [sampleVaultEnabled, setSampleVaultEnabled] = useState(false);
  // Phase 3: the public community-published-reports surface has its own flag (default OFF).
  const [vaultPublicReportsEnabled, setVaultPublicReportsEnabled] = useState(false);
  // Phase 4: the community malware exchange (controlled binary distribution). Default OFF.
  const [communityBinariesEnabled, setCommunityBinariesEnabled] = useState(false);
  useEffect(() => {
    api
      .platformStatus()
      .then((s) => {
        setSampleVaultEnabled(!!s.features?.sampleVault);
        setVaultPublicReportsEnabled(!!s.features?.sampleVaultPublicReports);
        setCommunityBinariesEnabled(!!s.features?.sampleVaultCommunityBinaries);
      })
      .catch(() => undefined);
  }, []);

  // Embed routes render bare (no app chrome) so they sit cleanly inside third-party iframes.
  if (location.pathname.startsWith('/embed/')) {
    return (
      <div className="min-h-screen bg-transparent p-2">
        <Routes>
          <Route path="/embed/:hash" element={<EmbedPage />} />
        </Routes>
      </div>
    );
  }

  // Grouped side-nav. Account is always present so Sign in / Dashboard / Admin never fall off
  // the chrome (anonymous users see Sign in; signed-in users see Workspace + Admin when entitled).
  const navGroups: { label: string; items: { to: string; label: string; accent?: boolean }[] }[] = [
    {
      label: 'Analyze',
      items: [
        { to: '/', label: 'Submit' },
        { to: '/scan', label: 'URL Scan' },
        { to: '/live', label: 'Live' },
      ],
    },
    {
      label: 'Intelligence',
      items: [
        { to: '/search', label: 'Search' },
        { to: '/graph', label: 'Investigate' },
        { to: '/diff', label: 'Compare' },
        { to: '/attack', label: 'ATT&CK' },
        { to: '/brands', label: 'Brand attacks' },
        { to: '/rules', label: 'Rules' },
        { to: '/firehose', label: 'Firehose' },
        { to: '/leaderboard', label: 'Leaderboard' },
        { to: '/agent', label: 'Agent', accent: true },
      ],
    },
    {
      label: 'Account',
      items: authed
        ? [
            { to: '/dashboard', label: 'Dashboard' },
            { to: '/alerts', label: 'Alerts', accent: true },
            { to: '/collections', label: 'Investigations' },
            ...(sampleVaultEnabled ? [{ to: '/vault', label: 'Vault' }] : []),
            ...(sampleVaultEnabled && communityBinariesEnabled ? [{ to: '/vault/community', label: 'Community exchange' }] : []),
            { to: '/billing', label: 'Billing' },
            ...(isPlatformAdmin ? [{ to: '/admin', label: 'Admin' }] : []),
          ]
        : [{ to: '/auth', label: 'Sign in' }],
    },
    {
      label: 'Resources',
      items: [
        { to: '/pricing', label: 'Pricing' },
        { to: '/docs', label: 'Docs' },
        { to: '/status', label: 'Status' },
        { to: '/community', label: 'Community' },
        ...(vaultPublicReportsEnabled ? [{ to: '/community/reports', label: 'Community reports' }] : []),
        { to: '/why', label: 'Why different' },
        { to: '/partners', label: 'Partner with us' },
      ],
    },
  ];

  // A nav item is active on an exact match, or when the current route is a child of it (except '/').
  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname === to || location.pathname.startsWith(`${to}/`);

  const handleSignOut = () => {
    setMenuOpen(false);
    void signOut();
    nav('/');
  };

  // Brand lockup for the navy chrome (sidebar + mobile bar): red mark, white wordmark.
  const brandLockup = (size: 'sm' | 'md') => (
    <div className="flex items-center gap-2.5">
      <Link to="/" onClick={() => setMenuOpen(false)} className="text-brand-cyble">
        <ShieldIcon size={size === 'md' ? 24 : 20} />
      </Link>
      <span className="flex flex-col leading-none">
        <Link
          to="/"
          onClick={() => setMenuOpen(false)}
          className={`font-semibold text-white ${size === 'md' ? 'text-body' : 'text-sm'}`}
        >
          MalwareAnalyzer
        </Link>
        {/* Cyble parent-brand attribution links out to cyble.com (kept out of the home <Link> to
            avoid nested anchors). */}
        <a
          href="https://www.cyble.com"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-0.5 text-[13px] font-medium text-white/60 transition-colors hover:text-white hover:underline"
        >
          by Cyble
        </a>
      </span>
    </div>
  );

  return (
    <div className="min-h-dvh">
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      {/* Global MalwareAnalyzer Agent chatbot - a floating launcher on every page (signed-in users). */}
      <AgentChatWidget />
      {/* First-run onboarding checklist (dismissible, once per browser). */}
      <OnboardingChecklist />

      {/* Mobile drawer backdrop */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setMenuOpen(false)}
          aria-hidden
        />
      )}

      {/* Left side menu: always fixed (viewport-pinned) on desktop so it never scrolls away;
          slide-in drawer below lg only. No transform on lg+ - transform on a fixed element
          can make the pane scroll with the page in some browsers. Main column: lg:pl-64. */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex h-dvh w-64 flex-col overflow-hidden bg-brand-navy transition-transform duration-200 ease-out ${
          menuOpen ? 'translate-x-0' : 'max-lg:-translate-x-full'
        }`}
      >
        <div className="flex h-16 shrink-0 items-center justify-between gap-2 border-b border-white/10 px-5">
          {brandLockup('md')}
          <NotificationBell />
        </div>

        <div className="px-3 pt-2">
          <button
            onClick={() => setPaletteOpen(true)}
            className="flex w-full items-center justify-between rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-sm text-white/60 transition-colors hover:border-white/30 hover:text-white"
          >
            <span className="flex items-center gap-2">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.3-4.3" />
              </svg>
              Search
            </span>
            <kbd className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-xs">⌘K</kbd>
          </button>
        </div>

        <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-2">
          {navGroups.map((group) => (
            <div key={group.label} className="mb-3 last:mb-0">
              <div className="px-3 pb-1 text-micro font-semibold uppercase tracking-wider text-white/45">
                {group.label}
              </div>
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const active = isActive(item.to);
                  return (
                    <Link
                      key={`${item.to}:${item.label}`}
                      to={item.to}
                      onClick={() => setMenuOpen(false)}
                      aria-current={active ? 'page' : undefined}
                      className={`relative block rounded-md px-3 py-1.5 text-base transition-colors ${
                        active
                          ? 'bg-white/10 font-medium text-white'
                          : 'text-white/70 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      {active && (
                        <span aria-hidden className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-cta" />
                      )}
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="shrink-0 space-y-2 border-t border-white/10 p-3">
          <ThemePicker />
          <Link
            to="/docs"
            onClick={() => setMenuOpen(false)}
            className="block w-full rounded-md bg-cta px-3 py-2 text-center text-sm font-semibold text-white transition-colors hover:bg-cta-hover"
          >
            Get API Access
          </Link>
          {authed ? (
            <button
              onClick={handleSignOut}
              className="block w-full rounded-md border border-white/30 px-3 py-2 text-center text-sm font-medium text-white transition-colors hover:bg-white/5"
            >
              Sign out
            </button>
          ) : (
            <Link
              to="/auth"
              onClick={() => setMenuOpen(false)}
              className="block w-full rounded-md border border-white/30 px-3 py-2 text-center text-sm font-medium text-white transition-colors hover:bg-white/5"
            >
              Sign in
            </Link>
          )}
        </div>
      </aside>

      {/* Main column offset for the fixed sidebar on desktop */}
      <div className="flex min-h-dvh flex-col bg-bg-base lg:pl-64">
        {/* Mobile top bar (hidden on desktop where the sidebar is always visible) */}
        <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-3 border-b border-white/10 bg-brand-navy px-4 lg:hidden">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="inline-flex items-center justify-center rounded-md border border-white/25 p-2 text-white/80 hover:text-white"
            aria-label="Toggle navigation menu"
            aria-expanded={menuOpen}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          {brandLockup('sm')}
          <div className="ml-auto">
            <NotificationBell />
          </div>
        </header>

        {/* The /graph investigation workbench manages its own viewport-height layout and uses the
            full main-column width on large monitors, so it opts out of the shared max-w-7xl cap. */}
        <main
          className={`w-full flex-1 overflow-x-hidden px-4 py-3 sm:px-6 sm:py-4 ${
            location.pathname === '/graph' ? '' : 'mx-auto max-w-7xl'
          }`}
        >
        <ErrorBoundary variant="route" resetKey={location.pathname}>
        {/* One entrance animation for every route, keyed to the path so it replays on navigation.
            Deliberately a 4px rise and a fade rather than a slide: enough to register as "this is
            new" without moving content far enough to be read as layout shift, and it is collapsed
            entirely by the global prefers-reduced-motion rule in styles.css. */}
        <div key={location.pathname} className="animate-rise-in">
        <Suspense fallback={<div className="py-6 text-center text-sm text-text-muted">Loading…</div>}>
        <Routes>
          <Route path="/" element={<SubmitPage />} />
          {/* `/submit` is the obvious URL for this page and had no route at all, so a shared,
              bookmarked or indexed link landed on NotFound even though the page exists at `/`. */}
          <Route path="/submit" element={<SubmitPage />} />
          <Route path="/scan" element={<ScanPage />} />
          {/* Canonical, indexable form of a completed scan. The host segment is a keyword slug and
              the uuid is the identifier, so a scan whose landed hostname later changes still
              resolves from an already-shared link. `/scan?uuid=` keeps working: it is embedded in
              exported reports and forwarded SIEM events that we cannot retroactively rewrite. */}
          <Route path="/scan/:host/:uuid" element={<ScanPage />} />
          <Route path="/report/:hash" element={<ReportPage />} />
          <Route path="/live" element={<LiveTaskPage />} />
          <Route path="/replay/:token" element={<ReplayPage />} />
          <Route path="/graph" element={<GraphExplorerPage />} />
          <Route path="/family/:name" element={<FamilyPage />} />
          {/* Must exist before the sitemap advertises /brand/<name>: a URL whose only implementation
              is a server-rendered twin sends every human click to NotFound. */}
          <Route path="/brand/:name" element={<BrandPage />} />
          {/* Order matters for humans reading this file, not the router: /brands/search is the
              noindex workbench, /brands the indexable directory above the /brand/<name> hubs. */}
          <Route path="/brands" element={<BrandsIndexPage />} />
          <Route path="/brands/search" element={<BrandsSearchPage />} />
          <Route path="/attack" element={<AttackNavigatorPage />} />
          {/* Indexed deep link (/attack/T1055 is in the sitemap and has an SSR twin), so it must
              resolve to a real page rather than the catch-all NotFound. */}
          <Route path="/attack/:technique" element={<AttackNavigatorPage />} />
          <Route path="/firehose" element={<FirehosePage />} />
          <Route path="/search" element={<SearchPage />} />
          {/* Topic landing pages. Each is indexed, sitemapped and has a server-rendered twin, so it
              must resolve here rather than falling through to NotFound. */}
          <Route path="/malware-analysis" element={<MalwareAnalysisPage />} />
          <Route path="/url-scanner" element={<UrlScannerPage />} />
          <Route path="/sandbox" element={<SandboxPage />} />
          <Route path="/threat-intelligence" element={<ThreatIntelligencePage />} />
          {/* `/seo/trends` has been server-rendering for crawlers with no page behind it. */}
          <Route path="/trends" element={<TrendsPage />} />
          <Route path="/agent" element={<AgentRoute />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/api-docs" element={<ApiDocsPage />} />
          <Route path="/docs" element={<ApiDocsPage />} />
          <Route path="/sdk" element={<ApiDocsPage />} />
          <Route path="/style-guide" element={<StyleGuidePage />} />
          <Route path="/collections" element={<CollectionsPage />} />
          <Route path="/collections/:id" element={<CollectionsPage />} />
          <Route path="/watch/:taskId" element={<TaskWatchPage />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/u/:handle" element={<ResearcherProfilePage />} />
          <Route path="/why" element={<ComparePage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/partners" element={<PartnerPage />} />
          {/* Any legacy /vs/<product> comparison URL folds into the single differentiation page. A
              wildcard rather than a named list so no competitor's name lives in our own URL space. */}
          <Route path="/vs/*" element={<Navigate to="/why" replace />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/link-device" element={<LinkDevicePage />} />
          {/* Direct-URL aliases so typed /register and /compare don't 404: registration lives on
              /auth (register mode) and sample comparison lives on /diff. */}
          <Route path="/register" element={<Navigate to="/auth?mode=register" replace />} />
          <Route path="/compare" element={<Navigate to="/diff" replace />} />
          <Route path="/verify" element={<VerifyPage />} />
          {/* Emailed deep links: password reset, invitation accept, email-change confirm. */}
          <Route path="/reset" element={<AccountActionPage action="reset" />} />
          <Route path="/invite" element={<AccountActionPage action="invite" />} />
          <Route path="/verify-email-change" element={<AccountActionPage action="email-change" />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          {/* Sample Vault (Phase 1). Always routable so deep links work; the page self-gates on the
              sample_vault_enabled flag (shows "not enabled" when off) + requires a signed-in user. */}
          <Route path="/vault" element={<VaultPage />} />
          {/* Phase 4: the community malware exchange. Self-gates on sampleVaultCommunityBinaries + auth. */}
          <Route path="/vault/community" element={<CommunityExchangePage />} />
          {/* Terms of Service + Acceptable Use Policy (public; required for community downloads). */}
          <Route path="/terms" element={<TermsPage />} />
          {/* Phase 3: public community-published vault reports. Self-gates on sampleVaultPublicReports. */}
          <Route path="/community/reports" element={<VaultReportsPage />} />
          <Route path="/alerts" element={<AlertsPage />} />
          <Route path="/diff" element={<DiffPage />} />
          <Route path="/rules" element={<RulesPage />} />
          <Route path="/campaign/:id" element={<CampaignPage />} />
          <Route
            path="/admin"
            element={
              isPlatformAdmin ? (
                <AdminOpsPage />
              ) : authed && !sessionRolesKnown() ? (
                // Cold-loading /admin races the async roles fetch: until /v1/auth/session has
                // answered, "no roles yet" means unknown, not denied - redirecting here bounced
                // real platform admins to /dashboard before their role arrived.
                <div className="px-6 py-16 text-center text-sm text-text-muted">Checking access…</div>
              ) : (
                <Navigate to={authed ? '/dashboard' : '/auth'} replace />
              )
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </div>
        </ErrorBoundary>
        </main>

        <footer className="bg-brand-navy text-white/70">
          <div className="mx-auto max-w-7xl px-6 py-5">
            <div className="flex flex-col gap-4 md:flex-row md:justify-between">
              <div className="max-w-xs">
                <div className="flex items-center gap-2.5">
                  <span className="text-brand-cyble">
                    <ShieldIcon size={24} />
                  </span>
                  <a
                    href="https://www.cyble.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-body font-semibold text-white transition-colors hover:text-white/80"
                  >
                    Cyble
                  </a>
                </div>
                <p className="mt-2 text-sm text-white/60">
                  MalwareAnalyzer, powered by{' '}
                  <a
                    href="https://www.cyble.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-white/80 transition-colors hover:text-white"
                  >
                    Cyble
                  </a>
                  .
                </p>
                <p className="mt-1 text-sm text-white/50">Malware analysis &amp; threat intelligence.</p>
              </div>
              <div className="grid grid-cols-2 gap-6 sm:grid-cols-3">
                <FooterColumn
                  title="Product"
                  links={[['/', 'Submit'], ['/scan', 'URL Scan'], ['/live', 'Live'], ['/graph', 'Investigate']]}
                />
                <FooterColumn
                  title="Intelligence"
                  links={[['/search', 'Search'], ['/firehose', 'Firehose'], ['/agent', 'Agent']]}
                />
                <FooterColumn
                  title="Resources"
                  links={[['/docs', 'Docs'], ['/status', 'Status'], ['/community', 'Community'], ['/partners', 'Partner with us'], ['/terms', 'Terms'], ['/style-guide', 'Style guide']]}
                />
              </div>
            </div>
            <div className="mt-8 flex flex-col gap-4 border-t border-white/10 pt-6 text-sm text-white/60 md:flex-row md:items-center md:justify-between">
              <div>
                &copy; {new Date().getFullYear()}{' '}
                <a
                  href="https://www.cyble.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-white/80 transition-colors hover:text-white"
                >
                  Cyble
                </a>
                . MalwareAnalyzer is a product from Cyble, US-headquartered.
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <TrustBadge label="Y Combinator W21" />
                <TrustBadge label="SOC 2 Type II" />
                <TrustBadge label="ISO 27001" />
              </div>
            </div>
            <p className="mt-4 text-micro text-white/40">
              Y Combinator W21, SOC 2 Type II, and ISO 27001 refer to certifications and affiliations held by Cyble.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}
